import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AddBudgetTransactionCategoryModal } from "@features/AddBudgetTransactionCategoryModal/AddBudgetTransactionCategoryModal";
import { Button } from "@components/Button";
import { FeatureFlag } from "@components/FeatureFlag/FeatureFlag";
import { PencilIcon, SquarePlusIcon } from "@components/icons";
import { Input } from "@components/inputs/Input/Input";
import { Radio } from "@components/inputs/Radio/Radio";
import { Select } from "@components/inputs/Select/Select";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useModalStore, type IUseModalProps } from "@store/modal/modal";
import { useEditTransactionCategoryMutation } from "@services/budget/mutations";
import {
  useBudgetMetadataQuery,
  useBudgetTransactionAvailableCategoriesQuery,
  useGetTransactionModificationActionCategories,
} from "@services/budget/queries";
import {
  TransactionType,
  type IBudgetTransaction,
} from "@services/budget/types";

interface IEditBudgetBreakdownCategoryModalProps extends IUseModalProps {
  transaction: IBudgetTransaction;
}

export const EditBudgetBreakdownCategoryModal: React.FC<
  IEditBudgetBreakdownCategoryModalProps
> = (props) => {
  const { transaction, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();
  const { create } = useModalStore();
  const { data: budgetMetadata } = useBudgetMetadataQuery();
  const { data: availableCategories, isLoading: isAvailableCategoriesLoading } =
    useBudgetTransactionAvailableCategoriesQuery({
      bankAccountTransactionId: transaction.bankAccountTransactionId,
    });
  const [selectedCategory, setSelectedCategory] = useState<string>(
    `${transaction.category.transactionCategoryId}_null`,
  );
  const [selectedModificationAction, setSelectedModificationAction] =
    useState<string>("ModifySelected");

  const { data: modificationActionCategoryData } =
    useGetTransactionModificationActionCategories();

  const { mutateAsync, isPending } = useEditTransactionCategoryMutation();

  // TODO: After select component supports object values, remove hack with string values
  const categoryOptions = useMemo(
    () =>
      availableCategories?.categories
        .map((category) => ({
          value: `${category.transactionCategoryId}_${category.transactionSubcategoryId}`,
          label:
            category.customerCategoryName ??
            (category.subcategorySlug
              ? `${t(`budget.category.${category.categorySlug}`)}: ${
                  category.subcategorySlug
                }`
              : t(`budget.category.${category.categorySlug}`)),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [availableCategories, t],
  );

  const radioOptions = modificationActionCategoryData?.actions.map(
    (action) => ({
      value: action,
      label: t(`budget.editCategoryModal.radio.${action}`),
    }),
  );

  const { toastApiError } = useApiErrorUtils();

  const onSubmit = async () => {
    const split = selectedCategory.split("_");

    const response = await mutateAsync({
      bankAccountTransactionId: transaction.bankAccountTransactionId,
      transactionCategoryId: parseInt(split[0]!),
      transactionSubcategoryId: parseInt(split[1]!),
      categoryModificationAction: selectedModificationAction,
    }).catch(toastApiError);

    if (response) {
      toast.success(t("budget.editCategoryModal.toast.success"));
      modal.close();
    }
  };

  const openAddCategoryModal = () => {
    create((modalProps) => (
      <AddBudgetTransactionCategoryModal {...modalProps} />
    ));
  };

  const canCreateOwnCategory =
    budgetMetadata?.canCreateOwnCategories &&
    transaction.payment.transactionTypeId === TransactionType.Credit;

  return (
    <Modal
      modal={modal}
      title={t("budget.editCategoryModal.title")}
      subtitle={t("budget.editCategoryModal.subtitle")}
      badgeProps={{ color: "green", icon: PencilIcon }}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("common.form.buttons.cancel")}
          </Button>
          <Button className="w-full" onClick={onSubmit} isLoading={isPending}>
            {t("common.form.buttons.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label={t("budget.editCategoryModal.form.receiver")}
          value={transaction.payment.partner}
          disabled
        ></Input>
        <Select
          isLoading={isAvailableCategoriesLoading}
          label={t("budget.editCategoryModal.form.category")}
          options={categoryOptions ?? []}
          onChange={(val) => val && setSelectedCategory(String(val))}
          value={selectedCategory}
        />
        <FeatureFlag flag={"isBudgetCustomCategoriesEnabled"}>
          {canCreateOwnCategory && (
            <React.Fragment>
              <span className="text-sm font-semibold">
                {t("budget.editCategoryModal.form.addCategory.label")}
              </span>
              <Button
                icon={SquarePlusIcon}
                variant="secondary"
                className="w-full"
                onClick={openAddCategoryModal}
              >
                {t("budget.editCategoryModal.form.addCategory.button")}
              </Button>
            </React.Fragment>
          )}
        </FeatureFlag>
        <Radio
          value={selectedModificationAction}
          defaultValue={"ModifySelected"}
          onRadioOptionChange={(value) =>
            setSelectedModificationAction(value as string)
          }
          className="pt-1"
          name="EditBudgetBreakdownCategoryModalRadio"
          options={radioOptions ?? []}
        />
      </div>
    </Modal>
  );
};
